import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { fmtNum, type Period } from "@am/shared";
import { toast } from "sonner";
import { useApiClient } from "../api/client";
import { PageHeader } from "../components/PageHeader";

interface OverviewResp {
  active_tenants?: number;
  total_requests?: number;
  unique_sessions?: number;
  estimated_cost_usd?: number;
  [k: string]: unknown;
}

interface TenantStatRow {
  tenant_id: string;
  tenant_name: string;
  total_requests: number;
  total_tokens: number;
}

const PERIODS: { key: Period; label: string }[] = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7j" },
  { key: "30d", label: "30j" },
];

export function OverviewPage() {
  const api = useApiClient();

  const [period, setPeriod] = useState<Period>("24h");
  const [overview, setOverview] = useState<OverviewResp | null>(null);
  const [rows, setRows] = useState<TenantStatRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [ov, tenants] = await Promise.all([
          api<OverviewResp>(`/admin/api/stats/overview?period=${period}`),
          api<TenantStatRow[]>(`/admin/api/stats/tenants?period=${period}`),
        ]);
        if (cancelled) return;
        setOverview(ov);
        setRows(tenants ?? []);
      } catch (err) {
        if (!cancelled) toast.error(err instanceof Error ? err.message : "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, period, reloadKey]);

  return (
    <div className="page is-active">
      <PageHeader
        layout="split"
        title={<>Vue d'<em>ensemble</em></>}
        subtitle="Métriques globales de la plateforme"
      >
        <div className="period-bar">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={"period-btn" + (period === p.key ? " is-active" : "")}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="stat-grid">
        <Stat label="Tenants actifs" value={loading ? "…" : fmtNum(overview?.active_tenants ?? 0)} valueClass="purple" />
        <Stat label="Requêtes" value={loading ? "…" : fmtNum(overview?.total_requests ?? 0)} />
        <Stat label="Sessions uniques" value={loading ? "…" : fmtNum(overview?.unique_sessions ?? 0)} />
        <Stat
          label="Coût estimé (USD)"
          value={loading ? "…" : `$${(overview?.estimated_cost_usd ?? 0).toFixed(4)}`}
          valueClass="green"
        />
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Activité par tenant</span>
          <button type="button" className="btn-sm btn-ghost" onClick={() => setReloadKey((k) => k + 1)} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={12} /> Actualiser
          </button>
        </div>
        <table>
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Requêtes</th>
              <th>Tokens</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="empty">
                  <span className="loading" />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="empty">
                  Aucune activité
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.tenant_id}>
                  <td>{row.tenant_name}</td>
                  <td>{fmtNum(row.total_requests)}</td>
                  <td>{fmtNum(row.total_tokens)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{label}</div>
      <div className={"stat-value" + (valueClass ? " " + valueClass : "")}>{value}</div>
    </div>
  );
}
